import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../composants/Button/Button';
import Banner from '../../../composants/Banner/Banner';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import OperationPreparationBC from '../../SuiviOperationBCV2/components/OperationPreparationBC';
import CommandesComplementaires from './CommandesComplementaires';
import MailPreview from '../../../composants/MailPreview/MailPreview';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import { getStatusColor } from '../../../utils/entityUtils';
import { buildBonCommandeMailPreview } from '../../../composants/MailPreview/bonCommandeMailTemplate';
import { ReactComponent as PdfIcon } from '../../../assets/icons/pdf.svg';
import { CommandeBC } from '../types';
import { telechargerBonCommande } from '../services/commandeBcService';
import { formatCommandeArticles, totalArticlesHT } from '../domain/commandeConsultation';
import { computeNotificationNextTabCta } from '../domain/commandeNextTab';
// Padding 4rem des sections + wrapper .consultation-bc, partages avec les autres onglets BC.
import './CommandeConsultation.css';
import './CommandeNotification.css';

interface CommandeNotificationProps {
  commande: CommandeBC;
  // Bascule vers la messagerie. Absente quand l'onglet n'est pas atteignable : le CTA se masque
  // alors de lui-meme (cf. computeNotificationNextTabCta).
  onGoToMessagerie?: () => void;
}

// Onglet "Notification et signature" PRESTATAIRE-BC (V2). Actif UNIQUEMENT quand le bon de commande
// est accepte/signe des deux cotes (statut Valide=7, gate en domaine commandeTabs). Miroir du
// "Notifications et signatures" du MS, mais ISOLE : aucun import depuis les modules MS. On y montre
// la previsualisation PDF du bon de commande signe (iframe, comme le MS) + le telechargement + le
// rappel des pieces de l'accord-cadre. Sections pliables (SectionPliable), comme les autres onglets.
const CommandeNotification: React.FC<CommandeNotificationProps> = ({ commande, onGoToMessagerie }) => {
  // Ref d'impression LOCALE : le parent ne passe pas de printRef a cet onglet.
  const printRef = useRef<HTMLDivElement>(null);
  const nextTabCta = computeNotificationNextTabCta(!!onGoToMessagerie);

  // external_id = numero du document (le backend BC lit le numero, pas l'id commande) ; fallback id.
  const externalId = commande.operation?.numero ?? commande.id;

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  // Blob du bon de commande signe -> URL objet pour l'iframe de previsualisation (revoquee au demontage).
  useEffect(() => {
    let annule = false;
    let objectUrl: string | null = null;
    setPdfError(false);
    setPdfUrl(null);
    telechargerBonCommande(externalId)
      .then((blob) => {
        if (annule) return;
        objectUrl = window.URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      })
      .catch(() => {
        if (!annule) setPdfError(true);
      });
    return () => {
      annule = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [externalId]);

  const [isMailOpen, setIsMailOpen] = useState(false);

  const op = commande.operation || {};
  const rang = commande.rang ?? 1;
  const dateTime = (v?: string | null): string =>
    v
      ? `${new Date(v).toLocaleDateString('fr-FR')} à ${new Date(v).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
        })}`
      : '';
  const accepteLe = commande.updated_at ? dateTime(commande.updated_at) : null;

  // Montant total HT (meme calcul que l'onglet Offre).
  const montantFr = totalArticlesHT(formatCommandeArticles(commande)).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Le "candidat" = le prestataire titulaire (destinataire de la notification). Son interlocuteur
  // porte le contact affiche dans le mail (le back template 12 derive interlocuteur_name/_phone du
  // prestataire via commande.type_compte/id_user, PAS de l'acheteur).
  const entite = commande.contrat?.entite as
    | { nom?: string; interlocuteur?: { prenom?: string; nom?: string; telephone?: string; email_contact?: string } }
    | undefined;
  const candidatNom = entite?.nom || 'Prestataire titulaire';
  // "Mail" du tableau = l'email du DESTINATAIRE (le prestataire) : c'est la que part la notification.
  const candidatMail = entite?.interlocuteur?.email_contact || '—';
  // Interlocuteur affiche DANS LE CORPS du mail = le CONTACT OPERATIONNEL ACHETEUR (celui que le
  // prestataire doit joindre). Cote presta, le back (showById) le met dans operation.acheteur
  // (top-level, resolu depuis operation.id_user/type_compte) ; repli sur organisme.acheteur (forme
  // acheteur) puis le signataire acheteur.
  const orgAcheteur = (op.organisme as
    | { acheteur?: { prenom?: string; nom?: string; telephone?: string } }
    | undefined)?.acheteur;
  const acheteurSig = (op as {
    acheteur_signataire?: { prenom?: string; nom?: string; telephone?: string };
  }).acheteur_signataire;
  const interlocuteur =
    [op.acheteur?.prenom, op.acheteur?.nom].filter(Boolean).join(' ') ||
    [orgAcheteur?.prenom, orgAcheteur?.nom].filter(Boolean).join(' ') ||
    [acheteurSig?.prenom, acheteurSig?.nom].filter(Boolean).join(' ');
  const interlocuteurTel =
    op.acheteur?.telephone || orgAcheteur?.telephone || acheteurSig?.telephone || '';

  // Mail Brevo 12 (notification du bon de commande au prestataire) rendu en apercu.
  const mailHtml = buildBonCommandeMailPreview({
    operationNumero: String(op.numero ?? commande.id),
    operationDate: dateTime(commande.created_at) || accepteLe || '',
    operationNom: op.nom || "l'opération",
    montant: montantFr,
    acceptationDate: accepteLe || '',
    interlocuteurName: interlocuteur,
    interlocuteurPhone: interlocuteurTel,
    dateEnvoi: accepteLe,
  });

  const handleDownload = useCallback(async () => {
    setInlineError(null);
    try {
      const blob = await telechargerBonCommande(externalId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const rawNumero = String(commande.operation?.numero ?? commande.id ?? 'document');
      const numeroSansP = rawNumero.startsWith('P') ? rawNumero.substring(1) : rawNumero;
      const annee = process.env.REACT_APP_ANNEE_EDITION || '25';
      link.download = `${annee}${numeroSansP} (signed).pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setInlineError(
        error instanceof Error
          ? error.message
          : 'Erreur lors du téléchargement du bon de commande. Veuillez réessayer.',
      );
    }
  }, [externalId, commande]);

  // Impression du mail (comme le MS) : iframe cachee sandbox (sans allow-scripts) -> print.
  const handlePrintMail = useCallback(() => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('sandbox', 'allow-same-origin allow-modals');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open();
    doc.write(mailHtml);
    doc.close();
    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      window.setTimeout(() => document.body.removeChild(iframe), 100);
    };
  }, [mailHtml]);

  // Banniere d'ETAT, rendue en haut ET en bas de l'onglet — meme dispositif que le MS et que la vue
  // acheteur : l'onglet est long, l'etat doit se lire sans remonter. Definie UNE fois : deux copies de
  // son JSX finiraient par se contredire.
  const stateBanner = (
    <Banner
      variant="success"
      filled
      message={`Bon de commande accepté et signé${accepteLe ? ` le ${accepteLe}` : ''}. Il est disponible ci-dessous. Les onglets de messagerie opérationnelle et d'évaluation du prestataire sont désormais actifs.`}
    />
  );

  // Bouton de telechargement defini UNE fois : rendu dans sa section ET dans la barre d'actions du
  // bas. `secondary` (blanc) : recuperer le document n'est pas l'action qui fait avancer le parcours.
  const boutonTelecharger = (
    <Button
      value="Télécharger le bon de commande signé"
      variant="secondary"
      size="medium"
      icon={<PdfIcon />}
      onClick={handleDownload}
    />
  );

  return (
    <div className="consultation-bc bc-notif" ref={printRef}>
      {/* En tete d'onglet, AVANT les sections : l'etat du bon de commande prime sur son detail. */}
      {stateBanner}

      {/* Recap de preparation. `titulaire={commande}` : le presta EST le titulaire, sa commande porte
          son contrat — il n'a pas la liste des commandes de l'operation pour chercher par rang. */}
      <OperationPreparationBC operation={op} titulaire={commande} />

      <SectionPliable title="Signature et notification" defaultOpen>
        <div className="section-content">
          <div className="bc-notif__preview">
            {/* Preview a 3/4 de la largeur (`--narrow`), comme le MS et la vue acheteur. */}
            <div className="bc-notif__pdf bc-notif__pdf--narrow">
              {pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  title="Prévisualisation du bon de commande signé"
                  className="bc-notif__iframe"
                />
              ) : pdfError ? (
                <div className="bc-notif__preview-empty">
                  La prévisualisation du bon de commande n'est pas disponible pour le moment. Vous
                  pouvez le télécharger ci-dessous.
                </div>
              ) : (
                <div className="bc-notif__preview-empty">Chargement de la prévisualisation…</div>
              )}
            </div>
          </div>

          {inlineError && (
            <div className="cmd-offre__inline-message cmd-offre__inline-message--error">
              {inlineError}
            </div>
          )}

          <div className="bc-notif__actions">{boutonTelecharger}</div>
        </div>
      </SectionPliable>

      <SectionPliable title="Information au candidat" defaultOpen>
        <div className="section-content">
          <div className="bc-notif__candidats">
            <div className="bc-notif__candidats-row bc-notif__candidats-head">
              <span>Prestataire (Rang {rang})</span>
              <span>Mail</span>
              <span>Contenu de la notification</span>
              <span>Notification</span>
            </div>
            <div className="bc-notif__candidats-row">
              <span className="bc-notif__candidats-nom">{candidatNom}</span>
              <span className="bc-notif__candidats-mail">{candidatMail}</span>
              <span>
                <button
                  type="button"
                  className="bc-notif__candidats-link"
                  onClick={() => setIsMailOpen(true)}
                >
                  Voir le message
                </button>
              </span>
              <span>{accepteLe ? `Notifié le ${accepteLe}` : '—'}</span>
            </div>
          </div>
        </div>
      </SectionPliable>

      <CommandesComplementaires commande={commande} />

      {/* Rappel de l'etat tout en bas. `no-print` : a l'impression il est deja en tete. */}
      <div className="no-print">{stateBanner}</div>

      {/* Apercu du mail de notification (template Brevo 12) rendu dans une iframe sandbox. */}
      {isMailOpen && (
        <div className="bc-notif__modal-overlay" onClick={() => setIsMailOpen(false)}>
          <div className="bc-notif__modal" onClick={(e) => e.stopPropagation()}>
            <div className="bc-notif__modal-bar">
              <span className="bc-notif__modal-title">
                {accepteLe ? `Envoyé le ${accepteLe}` : 'Aperçu du message'}
              </span>
              <div className="bc-notif__modal-actions">
                <Button value="PDF" variant="secondary" size="small" icon={<PdfIcon />} onClick={handlePrintMail} />
                <Button value="Fermer" variant="primary" size="small" onClick={() => setIsMailOpen(false)} />
              </div>
            </div>
            <MailPreview
              selectedCommandeForPreview={{ id: externalId }}
              generateMailPreview={() => mailHtml}
            />
          </div>
        </div>
      )}

      {/* Barre d'actions de bas d'onglet : impression + telechargement (duplique depuis sa section,
          meme JSX) + bascule vers la messagerie. Rendue en permanence — le CTA se masque seul. */}
      <div className="no-print bc-notif__actions">
        <PrintButton
          targetRef={printRef}
          fileName={`${commande.operation?.numero ?? commande.id} - Notification`}
          value="Imprimer la page"
          suiviHeaderProps={{
            titre: commande.operation?.nom || '',
            sousTitre: '',
            subtitle: `${commande.operation?.lot_technique?.id ?? ''} - ${commande.operation?.lot_technique?.nom ?? ''}`,
            status: commande.status?.nom || '',
            statusColor: getStatusColor(commande.status?.nom || ''),
          }}
        />
        {boutonTelecharger}
        <NextTabButton
          label={nextTabCta.label}
          hidden={nextTabCta.hidden}
          onClick={() => onGoToMessagerie?.()}
        />
      </div>
    </div>
  );
};

export default CommandeNotification;
